import type { UseMutationProps } from '@ztechapps/common.hook'
import { useActionMutation } from '@ztechapps/common.hook'
import { gql } from '@apollo/client'
import type { AutoPopulateProductMetadataArgs, Product } from '@ztechapps/common.contract'

export const autoPopulateProductMetadataQuery = gql`
  mutation autoPopulateProductMetadata($input: AutoPopulateProductMetadataArgs!) {
    autoPopulateProductMetadata(input: $input) {
      _id
      code
      metadata {
        _id
        key
        value
        unit
        description
      }
    }
  }
`

const defaultQuery = autoPopulateProductMetadataQuery

/**
 * Hook to auto-populate product metadata from product code
 *
 * @description
 * This hook provides a mutation function to automatically populate product metadata
 * (dimensions, shape, category, die set type) by parsing the product code.
 * It only populates missing metadata fields and preserves existing values.
 *
 * The feature must be enabled via the `product.autoPopulateSpecFromCode` app setting.
 *
 * @example
 * ```tsx
 * const [populateMetadata, { loading, error }] = useAutoPopulateProductMetadata()
 *
 * const handlePopulate = async () => {
 *   try {
 *     const product = await populateMetadata({ productId: 'product-123' })
 *     toast.showSuccess({ message: 'Metadata populated successfully' })
 *   } catch (err) {
 *     toast.showError({ message: err.message })
 *   }
 * }
 * ```
 *
 * @param props - Mutation configuration props
 * @returns Tuple containing [mutateFunction, mutationResult]
 */
export const useAutoPopulateProductMetadata = ({ query, ...props } = {} as UseMutationProps) => {
  query = query ?? defaultQuery

  return useActionMutation<Product, AutoPopulateProductMetadataArgs>({ query, ...props })
}
